import React, { useState, useEffect } from 'react';
import { canEditPayrollSettings } from '../../../utils/permissions';
import '../styles/PayrollSettings.css';
import {
  FiEdit2, FiSave, FiX, FiCheckCircle,
  FiAlertCircle, FiRefreshCw, FiCalendar,
  FiClock, FiSettings, FiLock
} from 'react-icons/fi';
import { gql, useQuery, useMutation } from '@apollo/client';

/**
 * Mask a sensitive value — shows first N and last N chars, hides the middle.
 * e.g. maskSensitive("301500") → "30****00"
 *      maskSensitive("WBC")    → "W**C"
 */
function maskSensitive(val, showFirst = 2, showLast = 2) {
  if (!val) return null;
  const s = String(val);
  if (s.length <= showFirst + showLast) {
    return s.length > 2 ? s[0] + '**' + s[s.length - 1] : s;
  }
  return s.slice(0, showFirst) + '****' + s.slice(-showLast);
}

function tConvert(time) {
  if (!time) return '';
  let [hours, minutes] = time.split(':');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = (hours % 12) || 12;
  return `${hours}:${minutes} ${ampm}`;
}

// ── GraphQL — Pay Rules ───────────────────────────────────────────────────────
const GET_PAY_RULES = gql`
  query PayRules {
    payRules {
      id
      type
      saturday
      sunday
      publicHoliday
      overtimeNormal
      overtimeSaturday
      overtimeSunday
      overtimePublicHoliday
      superannuation
      taxPayg
    }
  }
`;

const UPSERT_PAY_RULES = gql`
  mutation UpsertPayRules($input: UpsertPayRulesInput!) {
    upsertPayRules(input: $input) {
      id
      type
      saturday
      sunday
      publicHoliday
      overtimeNormal
      overtimeSaturday
      overtimeSunday
      overtimePublicHoliday
      superannuation
      taxPayg
    }
  }
`;

// ── GraphQL — Leave Rules ─────────────────────────────────────────────────────
const GET_LEAVE_RULES = gql`
  query LeaveRules {
    leaveRules {
      id
      type
      impact
      paid
      days
    }
  }
`;

const UPSERT_LEAVE_RULES = gql`
  mutation UpsertLeaveRules($input: UpsertLeaveRulesInput!) {
    upsertLeaveRules(input: $input) {
      id
      type
      impact
      paid
      days
    }
  }
`;

// ── GraphQL — Schedule ────────────────────────────────────────────────────────
const GET_PAYROLL_SCHEDULE = gql`
  query PayrollSchedule {
    payrollSchedule {
      id
      companyId
      frequency
      paydayDay
      paydayTime
      payPeriodStart
      payPeriodEnd
      isActive
      apcaId
      abaRemitterName
      abaEmployerBsb
      abaEmployerAccount
      abaAccountName
      abaBankAbbreviation
    }
  }
`;

const UPSERT_PAYROLL_SCHEDULE = gql`
  mutation UpsertPayrollSchedule($input: UpsertPayrollScheduleInput!) {
    upsertPayrollSchedule(input: $input) {
      id
      frequency
      paydayDay
      paydayTime
      payPeriodStart
      payPeriodEnd
      isActive
      apcaId
      abaRemitterName
      abaEmployerBsb
      abaEmployerAccount
      abaAccountName
      abaBankAbbreviation
    }
  }
`;

// ── GraphQL — Company ABA Details ────────────────────────────────────────────
const GET_COMPANY_ABA_DETAILS = gql`
  query CompanyAbaDetails {
    companyAbaDetails {
      id
      apcaId
      companyBsb
      companyAccountNumber
      companyAccountName
      bankAbbreviation
      remitterName
    }
  }
`;

const SAVE_COMPANY_ABA_DETAILS = gql`
  mutation SaveCompanyAbaDetails($input: SaveCompanyAbaDetailsInput!) {
    saveCompanyAbaDetails(input: $input) {
      id
      apcaId
      companyBsb
      companyAccountNumber
      companyAccountName
      bankAbbreviation
      remitterName
    }
  }
`;

// ── Column config ─────────────────────────────────────────────────────────────
const PAY_RULE_COLUMNS = [
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
  { key: 'publicHoliday', label: 'Public Holiday' },
  { key: 'overtimeNormal', label: 'Overtime Normal' },
  { key: 'overtimeSaturday', label: 'Overtime Saturday' },
  { key: 'overtimeSunday', label: 'Overtime Sunday' },
  { key: 'overtimePublicHoliday', label: 'Overtime Public Holiday' },
  { key: 'superannuation', label: 'Superannuation' },
  { key: 'taxPayg', label: 'Tax PAYG' },
];

// ── Toast hook ────────────────────────────────────────────────────────────────
function useToast() {
  const [toast, setToast] = useState(null);
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };
  return { toast, showToast };
}

// ── Main PayrollSettings Component ────────────────────────────────────────────
export default function PayrollSettings({ userRoles }) {
  const storedRoles       = JSON.parse(localStorage.getItem('userRoles'))       || [];
  const storedPermissions = JSON.parse(localStorage.getItem('userPermissions')) || [];
  const actualRoles       = userRoles?.length ? userRoles : storedRoles;
  const canEdit           = canEditPayrollSettings(actualRoles, storedPermissions);

  // ── Pay Rules state ──────────────────────────────────────────────────────────
  const [editRules, setEditRules] = useState(false);
  const [tempRules, setTempRules] = useState(null);

  // ── Schedule state ───────────────────────────────────────────────────────────
  const [tempSched, setTempSched] = useState(null);

  const { toast: rulesToast, showToast: showRulesToast } = useToast();

  const {
    data: rulesData,
    loading: rulesLoading,
    error: rulesError,
    refetch: refetchRules,
  } = useQuery(GET_PAY_RULES, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [upsertPayRules, { loading: savingRules }] = useMutation(UPSERT_PAY_RULES, {
    onCompleted: () => {
      setEditRules(false);
      setTempRules(null);
      refetchRules();
      showRulesToast('success', 'Pay rules saved successfully!');
    },
    onError: (err) => showRulesToast('error', err.message || 'Failed to save pay rules'),
  });

  const rules = rulesData?.payRules ?? [];
  const displayRules = editRules ? tempRules : rules;

  const startEditRules = () => { setTempRules(rules.map(r => ({ ...r }))); setEditRules(true); };
  const cancelEditRules = () => { setTempRules(null); setEditRules(false); };
  const handleRuleChange = (rowIdx, col, val) =>
    setTempRules(prev => prev.map((r, i) => i === rowIdx ? { ...r, [col]: val } : r));

  const saveRules = () => {
    const rulesInput = tempRules.map(({
      type, saturday, sunday, publicHoliday,
      overtimeNormal, overtimeSaturday, overtimeSunday,
      overtimePublicHoliday, superannuation, taxPayg,
    }) => ({
      type, saturday, sunday, publicHoliday,
      overtimeNormal, overtimeSaturday, overtimeSunday,
      overtimePublicHoliday, superannuation, taxPayg,
    }));
    upsertPayRules({ variables: { input: { rules: rulesInput } } });
  };

  const {
    data: schedData,
    refetch: refetchSched,
  } = useQuery(GET_PAYROLL_SCHEDULE, { fetchPolicy: 'cache-and-network', errorPolicy: 'all' });

  const [upsertSched, { loading: savingSched }] = useMutation(UPSERT_PAYROLL_SCHEDULE, {
    onCompleted: () => {
      setTempSched(null);
      refetchSched();
      showRulesToast('success', 'Autopay schedule saved successfully!');
    },
    onError: (err) => showRulesToast('error', err.message || 'Failed to save schedule'),
  });

  const schedule = schedData?.payrollSchedule || {
    frequency: 'weekly', // Auto Pay days are essentially weekly (Monday - Sunday)
    paydayDay: '',
    paydayTime: '12:01',
  };

  const currentSched = tempSched || schedule;

  const handleSchedChange = (changes) => {
    if (!tempSched) setTempSched({ ...schedule, ...changes });
    else setTempSched({ ...tempSched, ...changes });
  };

  const isSchedModified = tempSched !== null && (
    tempSched.frequency !== schedule.frequency ||
    tempSched.paydayDay !== schedule.paydayDay ||
    tempSched.paydayTime !== schedule.paydayTime
  );

  const cancelEditSched = () => { setTempSched(null); };

  const saveSched = () => {
    if (!tempSched) return;
    upsertSched({
      variables: {
        input: {
          frequency: tempSched.frequency,
          paydayDay: tempSched.paydayDay,
          paydayTime: tempSched.paydayTime,
        }
      }
    });
  };

  // ── ABA File Settings state (DB-backed via company_aba_details) ─────────────
  const [abaOpen, setAbaOpen] = useState(false);
  const [abaErrors, setAbaErrors] = useState({});
  const [abaForm, setAbaForm] = useState({
    apcaId: '', remitterName: '', companyBsb: '',
    companyAccountNumber: '', companyAccountName: '', bankAbbreviation: '',
  });

  const {
    data: abaData,
    refetch: refetchAba,
  } = useQuery(GET_COMPANY_ABA_DETAILS, { fetchPolicy: 'cache-and-network', errorPolicy: 'all' });

  const [saveCompanyAba, { loading: savingAba }] = useMutation(SAVE_COMPANY_ABA_DETAILS);

  // Sync form from DB whenever ABA details load
  useEffect(() => {
    const s = abaData?.companyAbaDetails;
    if (s) {
      setAbaForm({
        apcaId:               s.apcaId               || '',
        remitterName:         s.remitterName          || '',
        companyBsb:           s.companyBsb            || '',
        companyAccountNumber: s.companyAccountNumber  || '',
        companyAccountName:   s.companyAccountName    || '',
        bankAbbreviation:     s.bankAbbreviation      || '',
      });
    }
  }, [abaData]);

  const handleAbaChange = (field, val) => {
    setAbaForm(prev => ({ ...prev, [field]: val }));
    setAbaErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const saveAba = () => {
    const errors = {};

    if (!abaForm.apcaId?.trim()) {
      errors.apcaId = 'APCA ID is required';
    } else if (!/^\d{6}$/.test(abaForm.apcaId.trim())) {
      errors.apcaId = 'APCA ID must be exactly 6 digits (numbers only)';
    }

    if (!abaForm.companyBsb?.trim()) {
      errors.companyBsb = 'Company BSB is required';
    } else if (!/^\d{3}-\d{3}$/.test(abaForm.companyBsb.trim())) {
      errors.companyBsb = 'BSB must be in format 000-000';
    }

    if (!abaForm.companyAccountNumber?.trim()) {
      errors.companyAccountNumber = 'Account number is required';
    } else if (!/^\d{1,9}$/.test(abaForm.companyAccountNumber.trim())) {
      errors.companyAccountNumber = 'Account number must be numeric only, up to 9 digits';
    }

    if (!abaForm.companyAccountName?.trim()) {
      errors.companyAccountName = 'Account name is required';
    } else if (abaForm.companyAccountName.trim().length > 26) {
      errors.companyAccountName = 'Account name must be 26 characters or less';
    } else if (!/^[a-zA-Z0-9 ]+$/.test(abaForm.companyAccountName.trim())) {
      errors.companyAccountName = 'Account name may only contain letters, numbers, and spaces';
    }

    if (!abaForm.bankAbbreviation?.trim()) {
      errors.bankAbbreviation = 'Bank abbreviation is required';
    } else if (!/^[A-Z]{3}$/.test(abaForm.bankAbbreviation.trim())) {
      errors.bankAbbreviation = 'Bank abbreviation must be exactly 3 uppercase letters (e.g. WBC)';
    }

    if (!abaForm.remitterName?.trim()) {
      errors.remitterName = 'Remitter name is required';
    } else if (abaForm.remitterName.trim().length > 16) {
      errors.remitterName = 'Remitter name must be 16 characters or less';
    } else if (!/^[a-zA-Z0-9 ]+$/.test(abaForm.remitterName.trim())) {
      errors.remitterName = 'Remitter name may only contain letters, numbers, and spaces';
    }

    if (Object.keys(errors).length > 0) {
      setAbaErrors(errors);
      return;
    }

    setAbaErrors({});
    saveCompanyAba({
      variables: {
        input: {
          apcaId:               abaForm.apcaId.trim(),
          remitterName:         abaForm.remitterName.trim(),
          companyBsb:           abaForm.companyBsb.trim(),
          companyAccountNumber: abaForm.companyAccountNumber.trim(),
          companyAccountName:   abaForm.companyAccountName.trim(),
          bankAbbreviation:     abaForm.bankAbbreviation.trim().toUpperCase(),
        },
      },
      onCompleted: () => {
        refetchAba();
        setAbaOpen(false);
        showRulesToast('success', 'ABA settings saved!');
      },
      onError: (err) => setAbaErrors({ _form: err.message || 'Failed to save ABA settings' }),
    });
  };

  // ── Leave Rules state ────────────────────────────────────────────────────────
  const [editLeaves, setEditLeaves] = useState(false);
  const [tempLeaves, setTempLeaves] = useState(null);
  const { toast: leavesToast, showToast: showLeavesToast } = useToast();

  const {
    data: leavesData,
    loading: leavesLoading,
    error: leavesError,
    refetch: refetchLeaves,
  } = useQuery(GET_LEAVE_RULES, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [upsertLeaveRules, { loading: savingLeaves }] = useMutation(UPSERT_LEAVE_RULES, {
    onCompleted: () => {
      setEditLeaves(false);
      setTempLeaves(null);
      refetchLeaves();
      showLeavesToast('success', 'Leave rules saved successfully!');
    },
    onError: (err) => showLeavesToast('error', err.message || 'Failed to save leave rules'),
  });

  const leaves = leavesData?.leaveRules ?? [];
  const displayLeaves = editLeaves ? tempLeaves : leaves;

  const startEditLeaves = () => { setTempLeaves(leaves.map(l => ({ ...l }))); setEditLeaves(true); };
  const cancelEditLeaves = () => { setTempLeaves(null); setEditLeaves(false); };
  const handleLeaveChange = (idx, field, val) =>
    setTempLeaves(prev => prev.map((l, i) => i === idx ? { ...l, [field]: val } : l));

  const saveLeaves = () => {
    const rulesInput = tempLeaves.map(({ type, impact, paid, days }) => ({ type, impact, paid, days }));
    upsertLeaveRules({ variables: { input: { rules: rulesInput } } });
  };

  return (
    <div className="ps-page">

      {/* ── Page-level header ──────────────────────────────────────────────── */}
      <div className="ps-page-header">
        <h1 className="ps-page-title">Payroll Settings</h1>
        <p className="ps-page-subtitle">Configure salary rules and leave policies for your team</p>
      </div>

      {/* ── Pay Rules Section ──────────────────────────────────────────────── */}
      <div className="ps-section">
        <div className="ps-section-header">
          <div className="ps-section-title-group">
            <h2 className="ps-section-title">Pay Rules by Employment Type</h2>
            <p className="ps-section-subtitle">Define base pay rates and overtime rules for each employment type</p>
          </div>
          <div className="ps-header-right">
            {canEdit ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', background: '#f8fafc', padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <input
                  type="time"
                  className="ps-cell-input"
                  style={{ width: '100px', borderColor: '#cbd5e1', background: '#fff' }}
                  value={currentSched.paydayTime || ''}
                  onChange={e => handleSchedChange({ paydayTime: e.target.value })}
                />

                <select
                  className="ps-cell-input"
                  style={{ width: 'auto', borderColor: '#cbd5e1', background: '#fff', cursor: 'pointer' }}
                  value={currentSched.frequency || 'weekly'}
                  onChange={e => {
                    const freq = e.target.value;
                    handleSchedChange({ frequency: freq, paydayDay: freq === 'fortnightly' ? 'Monday' : '' });
                  }}
                >
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                </select>

                {(currentSched.frequency || 'weekly') === 'weekly' ? (
                  <select
                    className="ps-cell-input"
                    style={{ width: 'auto', borderColor: '#cbd5e1', background: '#fff', cursor: 'pointer' }}
                    value={currentSched.paydayDay || ''}
                    onChange={e => handleSchedChange({ paydayDay: e.target.value })}
                  >
                    <option value="" disabled>Day</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                ) : currentSched.frequency === 'fortnightly' ? (
                  <span className="ps-cell-input" style={{ width: 'auto', borderColor: '#cbd5e1', background: '#f1f5f9', color: '#475569', padding: '4px 10px', display: 'inline-flex', alignItems: 'center' }}>
                    Monday
                  </span>
                ) : (
                  <input
                    type="text"
                    placeholder="Date (1-31)"
                    className="ps-cell-input"
                    style={{ width: '120px', borderColor: '#cbd5e1', background: '#fff' }}
                    value={currentSched.paydayDay || ''}
                    onChange={e => handleSchedChange({ paydayDay: e.target.value })}
                  />
                )}

                {isSchedModified && (
                  <div style={{ display: 'flex', gap: '4px', marginLeft: '6px' }}>
                    <button className="ps-save-btn" onClick={saveSched} disabled={savingSched} style={{ padding: '6px 12px', fontSize: '12px' }}>
                      {savingSched ? 'Saving...' : 'Save'}
                    </button>
                    <button className="ps-cancel-btn" onClick={cancelEditSched} disabled={savingSched} style={{ padding: '6px 12px', fontSize: '12px' }}>Cancel</button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="ps-time-badge" title="Automated Pay Time">
                  <FiClock size={13} style={{ color: '#a8b8d8' }} />
                  <span>{schedule.paydayTime ? tConvert(schedule.paydayTime) : 'Not set'}</span>
                </div>
                <div className="ps-time-badge" title="Automated Pay Frequency & Day">
                  <FiCalendar size={13} style={{ color: '#a8b8d8' }} />
                  <span style={{ textTransform: 'capitalize' }}>
                    {schedule.frequency ? `${schedule.frequency}${schedule.paydayDay ? ` (${schedule.paydayDay})` : ''}` : 'Not set'}
                  </span>
                </div>
              </>
            )}

            {canEdit && !editRules && !rulesLoading && !rulesError && (
              <button className="ps-edit-btn" style={{ marginLeft: '12px' }} onClick={startEditRules}>
                <FiEdit2 size={13} /> Edit
              </button>
            )}
          </div>
        </div>

        {/* Toast */}
        {rulesToast && (
          <div className={`ps-toast ps-toast--${rulesToast.type}`}>
            <FiCheckCircle size={14} /> {rulesToast.msg}
          </div>
        )}

        {rulesLoading && !rulesData ? (
          <div className="ps-loading-state">
            <span className="ps-spinner" />
            Loading pay rules…
          </div>
        ) : rulesError ? (
          <div className="ps-error-state">
            <FiAlertCircle size={16} />
            <span>Failed to load pay rules.</span>
            <button className="ps-retry-btn" onClick={() => refetchRules()}>
              <FiRefreshCw size={13} /> Retry
            </button>
          </div>
        ) : (
          <>
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th className="ps-th-type">Pay Type</th>
                    {PAY_RULE_COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(displayRules ?? []).map((row, ri) => (
                    <tr key={row.type}>
                      <td className="ps-td-type">{row.type}</td>
                      {PAY_RULE_COLUMNS.map(col => (
                        <td key={col.key}>
                          {editRules ? (
                            <input
                              className="ps-cell-input"
                              type="text"
                              value={row[col.key] ?? ''}
                              onChange={e => handleRuleChange(ri, col.key, e.target.value)}
                            />
                          ) : (
                            <span className="ps-cell-value">{row[col.key]}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Notes box */}
            <div className="ps-notes">
              <div className="ps-notes-title">Notes:</div>
              <ul>
                <li>Overtime applies when worked &gt;8 hrs/day or outside rostered hours</li>
                <li>Minimum call-back pay: 3 hours (applies when employee is recalled)</li>
                <li>Casual loading compensates for no paid leave entitlements</li>
                <li>First 2 hours overtime on Mon-Sat uses OTN rate; after 2 hours uses 2× rate</li>
              </ul>
            </div>
          </>
        )}

        {editRules && (
          <div className="ps-save-bar">
            <button className="ps-cancel-btn" onClick={cancelEditRules} disabled={savingRules}>
              <FiX size={13} /> Cancel
            </button>
            <button className="ps-save-btn" onClick={saveRules} disabled={savingRules}>
              {savingRules ? <span className="ps-saving-spinner" /> : <><FiSave size={13} /> Save Changes</>}
            </button>
          </div>
        )}
      </div>

      {/* ── Leave Impact Section ────────────────────────────────────────────── */}
      <div className="ps-section">
        <div className="ps-section-header">
          <div className="ps-section-title-group">
            <h2 className="ps-section-title">Leave Impact</h2>
            <p className="ps-section-subtitle">How different leave types affect salary calculations</p>
          </div>
          {canEdit && !editLeaves && !leavesLoading && !leavesError && (
            <button className="ps-edit-btn" onClick={startEditLeaves}>
              <FiEdit2 size={13} /> Edit
            </button>
          )}
        </div>

        {/* Toast */}
        {leavesToast && (
          <div className={`ps-toast ps-toast--${leavesToast.type}`}>
            <FiCheckCircle size={14} /> {leavesToast.msg}
          </div>
        )}

        {leavesLoading && !leavesData ? (
          <div className="ps-loading-state">
            <span className="ps-spinner" />
            Loading leave rules…
          </div>
        ) : leavesError ? (
          <div className="ps-error-state">
            <FiAlertCircle size={16} />
            <span>Failed to load leave rules.</span>
            <button className="ps-retry-btn" onClick={() => refetchLeaves()}>
              <FiRefreshCw size={13} /> Retry
            </button>
          </div>
        ) : (
          <div className="ps-leave-table-wrap">
            <table className="ps-leave-table">
              <thead>
                <tr>
                  <th>Leave Type</th>
                  <th>Impact on Salary</th>
                  <th>Total Days of leave</th>
                </tr>
              </thead>
              <tbody>
                {(displayLeaves ?? []).map((leave, li) => (
                  <tr key={leave.type}>
                    <td className="ps-leave-type">{leave.type}</td>
                    <td>
                      {editLeaves ? (
                        <select
                          className="ps-leave-select"
                          value={leave.paid ? 'paid' : 'unpaid'}
                          onChange={e => {
                            const isPaid = e.target.value === 'paid';
                            handleLeaveChange(li, 'paid', isPaid);
                            handleLeaveChange(li, 'impact', isPaid ? 'Paid - No deduction' : 'Unpaid - Deduct salary');
                          }}
                        >
                          <option value="paid">Paid - No deduction</option>
                          <option value="unpaid">Unpaid - Deduct salary</option>
                        </select>
                      ) : (
                        <span className={leave.paid ? 'ps-badge-paid' : 'ps-badge-unpaid'}>
                          {leave.impact}
                        </span>
                      )}
                    </td>
                    <td className="ps-leave-days">
                      {editLeaves ? (
                        <input
                          className="ps-days-input"
                          type="text"
                          value={leave.days ?? ''}
                          onChange={e => handleLeaveChange(li, 'days', e.target.value)}
                        />
                      ) : leave.days}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editLeaves && (
          <div className="ps-save-bar">
            <button className="ps-cancel-btn" onClick={cancelEditLeaves} disabled={savingLeaves}>
              <FiX size={13} /> Cancel
            </button>
            <button className="ps-save-btn" onClick={saveLeaves} disabled={savingLeaves}>
              {savingLeaves ? <span className="ps-saving-spinner" /> : <><FiSave size={13} /> Save Changes</>}
            </button>
          </div>
        )}
      </div>

      {/* ── ABA File Settings (Admin only) ─────────────────────────────────── */}
      {canEdit && (
        <div className="ps-section">
          <div className="ps-section-header">
            <div className="ps-section-title-group">
              <h2 className="ps-section-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FiLock size={14} style={{ color: '#6366f1' }} /> ABA File Settings
                <span style={{ fontSize: '10px', background: '#ede9fe', color: '#6366f1', padding: '2px 7px', borderRadius: '20px', fontWeight: 600, marginLeft: '4px' }}>Admin Only</span>
              </h2>
              <p className="ps-section-subtitle">APCA Direct Entry details required to generate ABA payment files for your bank</p>
            </div>
            <div className="ps-header-right">
              <button
                className="ps-edit-btn"
                onClick={() => { setAbaOpen(o => !o); setAbaErrors({}); }}
                style={{ background: abaOpen ? '#ede9fe' : '', color: abaOpen ? '#6366f1' : '', borderColor: abaOpen ? '#c4b5fd' : '' }}
              >
                <FiSettings size={13} /> {abaOpen ? 'Close' : 'Configure'}
              </button>
            </div>
          </div>

          {/* Current saved values — masked for security */}
          {!abaOpen && (() => {
            const s = abaData?.companyAbaDetails;
            const notSet = <span style={{ color: '#94a3b8' }}>Not set</span>;
            return (
              <div style={{ padding: '12px 16px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div className="ps-aba-field-row">
                  <span className="ps-aba-label">APCA ID</span>
                  <span className="ps-aba-value">{maskSensitive(s?.apcaId) || notSet}</span>
                </div>
                <div className="ps-aba-field-row">
                  <span className="ps-aba-label">Remitter Name</span>
                  <span className="ps-aba-value">{s?.remitterName || notSet}</span>
                </div>
                <div className="ps-aba-field-row">
                  <span className="ps-aba-label">Company BSB</span>
                  <span className="ps-aba-value">{maskSensitive(s?.companyBsb) || notSet}</span>
                </div>
                <div className="ps-aba-field-row">
                  <span className="ps-aba-label">Account Number</span>
                  <span className="ps-aba-value">{maskSensitive(s?.companyAccountNumber) || notSet}</span>
                </div>
                <div className="ps-aba-field-row">
                  <span className="ps-aba-label">Account Name</span>
                  <span className="ps-aba-value">{s?.companyAccountName || notSet}</span>
                </div>
                <div className="ps-aba-field-row">
                  <span className="ps-aba-label">Bank</span>
                  <span className="ps-aba-value">{maskSensitive(s?.bankAbbreviation, 1, 1) || notSet}</span>
                </div>
              </div>
            );
          })()}

          {/* Inline edit form */}
          {abaOpen && (
            <div style={{ padding: '16px', borderTop: '1px solid #eef2f7' }}>
              {abaErrors._form && (
                <div style={{ marginBottom: '12px', padding: '8px 12px', background: '#fef2f2', color: '#dc2626', borderRadius: '6px', fontSize: '12px', display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <FiAlertCircle size={13} /> {abaErrors._form}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', marginBottom: '16px' }}>
                <div className="ps-aba-form-group">
                  <label className="ps-aba-form-label">APCA ID <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    className="ps-cell-input"
                    style={{ width: '100%', boxSizing: 'border-box', borderColor: abaErrors.apcaId ? '#dc2626' : undefined }}
                    type="text"
                    maxLength={6}
                    placeholder="e.g. 301500"
                    value={abaForm.apcaId}
                    onChange={e => handleAbaChange('apcaId', e.target.value)}
                  />
                  {abaErrors.apcaId
                    ? <span style={{ fontSize: '10px', color: '#dc2626', marginTop: '3px', display: 'block' }}>{abaErrors.apcaId}</span>
                    : <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>Exactly 6 digits — issued by AusPayNet</span>
                  }
                </div>
                <div className="ps-aba-form-group">
                  <label className="ps-aba-form-label">Remitter Name <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    className="ps-cell-input"
                    style={{ width: '100%', boxSizing: 'border-box', borderColor: abaErrors.remitterName ? '#dc2626' : undefined }}
                    type="text"
                    maxLength={16}
                    placeholder="e.g. ACME PTY LTD"
                    value={abaForm.remitterName}
                    onChange={e => handleAbaChange('remitterName', e.target.value)}
                  />
                  {abaErrors.remitterName
                    ? <span style={{ fontSize: '10px', color: '#dc2626', marginTop: '3px', display: 'block' }}>{abaErrors.remitterName}</span>
                    : <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>Max 16 chars — letters, numbers, spaces only</span>
                  }
                </div>
                <div className="ps-aba-form-group">
                  <label className="ps-aba-form-label">Company BSB <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    className="ps-cell-input"
                    style={{ width: '100%', boxSizing: 'border-box', borderColor: abaErrors.companyBsb ? '#dc2626' : undefined }}
                    type="text"
                    maxLength={7}
                    placeholder="e.g. 062-000"
                    value={abaForm.companyBsb}
                    onChange={e => handleAbaChange('companyBsb', e.target.value)}
                  />
                  {abaErrors.companyBsb
                    ? <span style={{ fontSize: '10px', color: '#dc2626', marginTop: '3px', display: 'block' }}>{abaErrors.companyBsb}</span>
                    : <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>Format: 000-000 (digits and hyphen only)</span>
                  }
                </div>
                <div className="ps-aba-form-group">
                  <label className="ps-aba-form-label">Account Number <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    className="ps-cell-input"
                    style={{ width: '100%', boxSizing: 'border-box', borderColor: abaErrors.companyAccountNumber ? '#dc2626' : undefined }}
                    type="text"
                    maxLength={9}
                    placeholder="e.g. 123456789"
                    value={abaForm.companyAccountNumber}
                    onChange={e => handleAbaChange('companyAccountNumber', e.target.value)}
                  />
                  {abaErrors.companyAccountNumber
                    ? <span style={{ fontSize: '10px', color: '#dc2626', marginTop: '3px', display: 'block' }}>{abaErrors.companyAccountNumber}</span>
                    : <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>Numeric only, up to 9 digits</span>
                  }
                </div>
                <div className="ps-aba-form-group">
                  <label className="ps-aba-form-label">Account Name <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    className="ps-cell-input"
                    style={{ width: '100%', boxSizing: 'border-box', borderColor: abaErrors.companyAccountName ? '#dc2626' : undefined }}
                    type="text"
                    maxLength={26}
                    placeholder="e.g. ACME PTY LTD"
                    value={abaForm.companyAccountName}
                    onChange={e => handleAbaChange('companyAccountName', e.target.value)}
                  />
                  {abaErrors.companyAccountName
                    ? <span style={{ fontSize: '10px', color: '#dc2626', marginTop: '3px', display: 'block' }}>{abaErrors.companyAccountName}</span>
                    : <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>Max 26 chars — letters, numbers, spaces only</span>
                  }
                </div>
                <div className="ps-aba-form-group">
                  <label className="ps-aba-form-label">Bank Abbreviation <span style={{ color: '#dc2626' }}>*</span></label>
                  <input
                    className="ps-cell-input"
                    style={{ width: '100%', boxSizing: 'border-box', borderColor: abaErrors.bankAbbreviation ? '#dc2626' : undefined }}
                    type="text"
                    maxLength={3}
                    placeholder="e.g. WBC"
                    value={abaForm.bankAbbreviation}
                    onChange={e => handleAbaChange('bankAbbreviation', e.target.value.toUpperCase())}
                  />
                  {abaErrors.bankAbbreviation
                    ? <span style={{ fontSize: '10px', color: '#dc2626', marginTop: '3px', display: 'block' }}>{abaErrors.bankAbbreviation}</span>
                    : <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>Exactly 3 uppercase letters (e.g. WBC, CBA, ANZ)</span>
                  }
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button className="ps-cancel-btn" onClick={() => {
                  const s = abaData?.companyAbaDetails;
                  setAbaForm({
                    apcaId:               s?.apcaId               || '',
                    remitterName:         s?.remitterName          || '',
                    companyBsb:           s?.companyBsb            || '',
                    companyAccountNumber: s?.companyAccountNumber  || '',
                    companyAccountName:   s?.companyAccountName    || '',
                    bankAbbreviation:     s?.bankAbbreviation      || '',
                  });
                  setAbaOpen(false);
                  setAbaErrors({});
                }}>
                  <FiX size={13} /> Cancel
                </button>
                <button className="ps-save-btn" onClick={saveAba} disabled={savingAba}>
                  {savingAba ? 'Saving...' : <><FiSave size={13} /> Save ABA Settings</>}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
